/**
 * Lab bet-ticker multi ui-select — pick several members via searchHierarchyNew.
 * Tagging: Enter adds the typed name; click picks a suggestion.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Spinner } from '@w7/ui'
import { searchHierarchyNew } from '@/api/map/member'

const DEBOUNCE_MS = 100
const SUGGEST_LIMIT = 20

export type TickerMemberSearchProps = {
  value: string[]
  onChange: (usernames: string[]) => void
  /** Same as former Clear Members — clears selected members (+ parent search). */
  onClear?: () => void
  canClear?: boolean
}

export function TickerMemberSearch({
  value,
  onChange,
  onClear,
  canClear = false,
}: TickerMemberSearchProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const root = rootRef.current
      if (root && !root.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const membersQ = useQuery({
    queryKey: ['cm', 'bet-ticker', 'member-search', debounced],
    queryFn: async () => {
      const rows = await searchHierarchyNew({ filter: debounced.trim(), page: 1 })
      return rows.map((r) => r.username).filter(Boolean)
    },
    enabled: open,
    staleTime: 10_000,
    retry: false,
  })

  const selected = new Set(value.map((u) => u.toLowerCase()))
  const suggestions = (membersQ.data ?? [])
    .filter((name) => !selected.has(name.toLowerCase()))
    .slice(0, SUGGEST_LIMIT)

  const pick = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed || selected.has(trimmed.toLowerCase())) return
    onChange([...value, trimmed])
    setDraft('')
    setOpen(true)
  }

  const remove = (name: string) => {
    onChange(value.filter((u) => u !== name))
  }

  return (
    <div className="w7-bm-member-search" ref={rootRef} data-testid="ticker-member-search">
      <div className="w7-bm-member-chips" onFocusCapture={() => setOpen(true)}>
        {value.map((name) => (
          <button
            key={name}
            type="button"
            className="w7-bm-member-chip"
            onClick={() => remove(name)}
            aria-label={`Remove ${name}`}
            title="Remove"
          >
            {name} ×
          </button>
        ))}
        <input
          type="text"
          className="w7-bm-member-input"
          aria-label="Search members"
          placeholder="Enter or Select Member Name to Search"
          value={draft}
          aria-expanded={open}
          aria-controls="w7-bm-member-suggest"
          aria-autocomplete="list"
          aria-busy={membersQ.isFetching}
          onChange={(e) => {
            setDraft(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (draft.trim()) pick(draft)
            }
            if (e.key === 'Escape') setOpen(false)
          }}
        />
      </div>
      {onClear && canClear ? (
        <button
          type="button"
          className="w7-bm-member-clear"
          aria-label="Clear members"
          title="Clear members"
          data-testid="clear-members-btn"
          onClick={() => {
            setDraft('')
            onClear()
          }}
        >
          ×
        </button>
      ) : null}

      {open ? (
        <ul
          id="w7-bm-member-suggest"
          className="w7-bm-member-suggest"
          role="listbox"
          aria-label="Member suggestions"
        >
          {membersQ.isFetching && suggestions.length === 0 ? (
            <li className="w7-bm-member-suggest-empty" role="presentation">
              <Spinner size={14} /> Searching…
            </li>
          ) : null}
          {membersQ.isError ? (
            <li className="w7-bm-member-suggest-empty" role="presentation">
              Could not load members — check CM-API / login.
            </li>
          ) : null}
          {suggestions.map((name) => (
            <li key={name} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w7-bm-member-suggest-option"
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(name)
                }}
              >
                {name}
              </button>
            </li>
          ))}
          {!membersQ.isFetching && suggestions.length === 0 && !membersQ.isError ? (
            <li className="w7-bm-member-suggest-empty" role="presentation">
              No members match “{draft.trim() || '…'}”.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  )
}
